import api from "./api";
import type { User, Department, Role } from "@/types/user";
import type {
  SystemStats,
  AuditLogListResponse,
  AuditLogFilters,
  SystemSetting,
  SystemSettingsResponse,
  HealthCheckResponse,
  UserManageFilters,
  UserListResponse,
  UserCreateRequest,
  UserUpdateRequest,
  DepartmentCreateRequest,
  DepartmentUpdateRequest,
  Permission,
  RoleCreateRequest,
  RoleUpdateRequest,
  PermissionGroup,
  PermissionGroupCreateRequest,
  PermissionGroupUpdateRequest,
  MenuItem,
  MenuCreateRequest,
  MenuUpdateRequest,
  MenuReorderRequest,
  UserPermissions,
  SetUserGroupsRequest,
  SetUserMenuPermissionsRequest,
  UserWithIndividualPermissions,
  BulkMenuPermissionsRequest,
  BulkRemoveMenuPermissionsRequest,
  NotificationStats,
  NotificationAdminListResponse,
  NotificationAdminFilters,
  PushSubscriptionListResponse,
  PushSubscriptionFilters,
  EmailLogListResponse,
  EmailLogFilters,
  EmailLogStats,
  ConsentRecordListResponse,
  ConsentRecordFilters,
  ConsentDocument,
  ConsentDocumentCreate,
  ConsentDocumentUpdate,
  ConsentDocumentListResponse,
} from "@/types/admin";

export const adminService = {
  // === 통계 ===

  // 시스템 통계 조회
  async getSystemStats(): Promise<SystemStats> {
    const response = await api.get<SystemStats>("/admin/stats");
    return response.data;
  },

  // === 사용자 관리 ===

  // 사용자 목록 조회
  async getUsers(params?: UserManageFilters): Promise<UserListResponse> {
    const response = await api.get<UserListResponse>("/admin/users", { params });
    return response.data;
  },

  // 사용자 상세 조회
  async getUser(userId: string): Promise<User> {
    const response = await api.get<User>(`/admin/users/${userId}`);
    return response.data;
  },

  // 사용자 생성
  async createUser(data: UserCreateRequest): Promise<User> {
    const response = await api.post<User>("/admin/users", data);
    return response.data;
  },

  // 사용자 수정
  async updateUser(userId: string, data: UserUpdateRequest): Promise<User> {
    const response = await api.put<User>(`/admin/users/${userId}`, data);
    return response.data;
  },

  // 사용자 삭제
  async deleteUser(userId: string): Promise<void> {
    await api.delete(`/admin/users/${userId}`);
  },

  // 사용자 비밀번호 초기화
  async resetUserPassword(
    userId: string
  ): Promise<{ temporary_password: string }> {
    const response = await api.post<{ temporary_password: string }>(
      `/admin/users/${userId}/reset-password`
    );
    return response.data;
  },

  // 대리 관리 직원 → 일반 계정 전환 (임시 비밀번호 1회 응답)
  async activateManagedAccount(
    userId: string,
    email: string
  ): Promise<{ id: string; email: string; name: string; temporary_password: string }> {
    const response = await api.post(`/admin/users/${userId}/activate-account`, { email });
    return response.data;
  },

  // 일반 직원 → 대리 관리 계정 전환 (로그인 차단)
  async convertToManaged(userId: string): Promise<User> {
    const response = await api.post<User>(`/admin/users/${userId}/convert-to-managed`);
    return response.data;
  },

  // 사용자 활성화/비활성화
  async toggleUserStatus(userId: string, isActive: boolean): Promise<User> {
    const response = await api.put<User>(`/admin/users/${userId}/status`, {
      is_active: isActive,
    });
    return response.data;
  },

  // === 부서 관리 ===

  // 부서 목록 조회 (company_id: super_admin 전용 대상 기업 지정)
  async getDepartments(company_id?: string): Promise<Department[]> {
    const response = await api.get<Department[]>("/admin/departments", {
      params: { company_id },
    });
    return response.data;
  },

  // 부서 상세 조회
  async getDepartment(departmentId: number): Promise<Department> {
    const response = await api.get<Department>(
      `/admin/departments/${departmentId}`
    );
    return response.data;
  },

  // 부서 생성
  async createDepartment(data: DepartmentCreateRequest, company_id?: string): Promise<Department> {
    const response = await api.post<Department>("/admin/departments", data, {
      params: { company_id },
    });
    return response.data;
  },

  // 부서 수정
  async updateDepartment(
    departmentId: number,
    data: DepartmentUpdateRequest,
    company_id?: string
  ): Promise<Department> {
    const response = await api.put<Department>(
      `/admin/departments/${departmentId}`,
      data,
      { params: { company_id } }
    );
    return response.data;
  },

  // 부서 삭제
  async deleteDepartment(departmentId: number, company_id?: string): Promise<void> {
    await api.delete(`/admin/departments/${departmentId}`, {
      params: { company_id },
    });
  },

  // === 역할 관리 ===

  // 역할 목록 조회
  async getRoles(): Promise<Role[]> {
    const response = await api.get<Role[]>("/admin/roles");
    return response.data;
  },

  // 역할 상세 조회
  async getRole(roleId: number): Promise<Role> {
    const response = await api.get<Role>(`/admin/roles/${roleId}`);
    return response.data;
  },

  // 역할 생성
  async createRole(data: RoleCreateRequest): Promise<Role> {
    const response = await api.post<Role>("/admin/roles", data);
    return response.data;
  },

  // 역할 수정
  async updateRole(roleId: number, data: RoleUpdateRequest): Promise<Role> {
    const response = await api.put<Role>(`/admin/roles/${roleId}`, data);
    return response.data;
  },

  // 역할 삭제
  async deleteRole(roleId: number): Promise<void> {
    await api.delete(`/admin/roles/${roleId}`);
  },

  // 권한 목록 조회
  async getPermissions(): Promise<Permission[]> {
    const response = await api.get<Permission[]>("/admin/permissions");
    return response.data;
  },

  // === 권한 그룹 관리 ===

  // 권한 그룹 목록 조회 (트리 구조) (company_id: super_admin 전용 대상 기업 지정)
  async getPermissionGroups(company_id?: string): Promise<PermissionGroup[]> {
    const response = await api.get<PermissionGroup[]>("/admin/permission-groups", {
      params: { company_id },
    });
    return response.data;
  },

  // 권한 그룹 상세 조회
  async getPermissionGroup(groupId: number): Promise<PermissionGroup> {
    const response = await api.get<PermissionGroup>(`/admin/permission-groups/${groupId}`);
    return response.data;
  },

  // 권한 그룹 생성
  async createPermissionGroup(data: PermissionGroupCreateRequest, company_id?: string): Promise<PermissionGroup> {
    const response = await api.post<PermissionGroup>("/admin/permission-groups", data, {
      params: { company_id },
    });
    return response.data;
  },

  // 권한 그룹 수정
  async updatePermissionGroup(groupId: number, data: PermissionGroupUpdateRequest, company_id?: string): Promise<PermissionGroup> {
    const response = await api.put<PermissionGroup>(`/admin/permission-groups/${groupId}`, data, {
      params: { company_id },
    });
    return response.data;
  },

  // 권한 그룹 삭제
  async deletePermissionGroup(groupId: number, company_id?: string): Promise<void> {
    await api.delete(`/admin/permission-groups/${groupId}`, {
      params: { company_id },
    });
  },

  // 그룹의 메뉴 권한 조회
  async getGroupMenus(groupId: number, includeInherited?: boolean, company_id?: string): Promise<number[]> {
    const response = await api.get<number[]>(`/admin/permission-groups/${groupId}/menus`, {
      params: { include_inherited: includeInherited, company_id },
    });
    return response.data;
  },

  // 그룹의 메뉴 권한 조회 (상속 구분)
  async getGroupMenusWithInheritance(groupId: number, company_id?: string): Promise<{
    direct_menu_ids: number[];
    inherited_menus: {
      [groupId: string]: {
        name: string;
        menu_ids: number[];
      };
    };
  }> {
    const response = await api.get(`/admin/permission-groups/${groupId}/menus-with-inheritance`, {
      params: { company_id },
    });
    return response.data;
  },

  // 그룹의 메뉴 권한 설정
  // visibleMenuIds: 저장 화면에 렌더된(체크/해제 가능했던) 시스템 메뉴 id 목록.
  // 서버는 이 범위 밖의 기존 권한을 보존한다(화면에 없던 메뉴 권한이 저장 시 소실되는 문제 방지).
  async setGroupMenus(
    groupId: number,
    menuIds: number[],
    customOrder: boolean = false,
    company_id?: string,
    visibleMenuIds?: number[]
  ): Promise<void> {
    await api.put(
      `/admin/permission-groups/${groupId}/menus`,
      { menu_ids: menuIds, custom_order: customOrder, visible_menu_ids: visibleMenuIds },
      { params: { company_id } }
    );
  },

  // 권한 그룹 설정용 기업 메뉴 목록 조회
  async getCompanyMenusForPermissions(company_id?: string): Promise<MenuItem[]> {
    const response = await api.get<MenuItem[]>("/admin/company-menus-for-permissions", {
      params: { company_id },
    });
    return response.data;
  },

  // === 메뉴 관리 ===

  // 메뉴 목록 조회 (평면 배열 또는 트리 구조)
  async getMenus(params?: { flat?: boolean; company_id?: string }): Promise<MenuItem[]> {
    const response = await api.get<MenuItem[]>("/admin/menus", {
      params: { flat: params?.flat ?? true, company_id: params?.company_id },
    });
    return response.data;
  },

  // 메뉴 상세 조회
  async getMenu(menuId: number): Promise<MenuItem> {
    const response = await api.get<MenuItem>(`/admin/menus/${menuId}`);
    return response.data;
  },

  // 메뉴 생성
  async createMenu(data: MenuCreateRequest, company_id?: string): Promise<MenuItem> {
    const response = await api.post<MenuItem>("/admin/menus", data, {
      params: { company_id },
    });
    return response.data;
  },

  // 메뉴 수정
  async updateMenu(menuId: number, data: MenuUpdateRequest, company_id?: string): Promise<MenuItem> {
    const response = await api.put<MenuItem>(`/admin/menus/${menuId}`, data, {
      params: { company_id },
    });
    return response.data;
  },

  // 메뉴 삭제
  async deleteMenu(menuId: number, company_id?: string): Promise<void> {
    await api.delete(`/admin/menus/${menuId}`, {
      params: { company_id },
    });
  },

  // 메뉴 순서 변경
  async reorderMenus(items: MenuReorderRequest[]): Promise<void> {
    await api.post("/admin/menus/reorder", { items });
  },

  // === 사용자 권한 관리 ===

  // 사용자의 권한 정보 조회
  async getUserPermissions(userId: string): Promise<UserPermissions> {
    const response = await api.get<UserPermissions>(`/admin/users/${userId}/permissions`);
    return response.data;
  },

  // 사용자의 권한 그룹 설정 (단일)
  async setUserGroup(userId: string, data: SetUserGroupsRequest): Promise<void> {
    await api.put(`/admin/users/${userId}/permission-groups`, data);
  },

  // 사용자의 개인별 메뉴 권한 설정
  async setUserMenuPermissions(userId: string, data: SetUserMenuPermissionsRequest): Promise<void> {
    await api.put(`/admin/users/${userId}/menu-permissions`, data);
  },

  // 개인별 메뉴 권한을 가진 사용자 목록 조회
  async getUsersWithIndividualPermissions(): Promise<UserWithIndividualPermissions[]> {
    const response = await api.get<UserWithIndividualPermissions[]>("/admin/users/with-individual-permissions");
    return response.data;
  },

  // 대량 개인별 메뉴 권한 설정
  async setBulkMenuPermissions(data: BulkMenuPermissionsRequest): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>("/admin/users/bulk-menu-permissions", data);
    return response.data;
  },

  // 대량 개인별 메뉴 권한 제거
  async removeBulkMenuPermissions(data: BulkRemoveMenuPermissionsRequest): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>("/admin/users/bulk-menu-permissions", { data });
    return response.data;
  },

  // === 감사 로그 ===

  // 감사 로그 목록 조회
  async getAuditLogs(params?: AuditLogFilters): Promise<AuditLogListResponse> {
    const response = await api.get<AuditLogListResponse>("/admin/audit-logs", {
      params,
    });
    return response.data;
  },

  // 감사 로그 내보내기
  async exportAuditLogs(params?: AuditLogFilters): Promise<Blob> {
    const response = await api.get("/admin/audit-logs/export", {
      params,
      responseType: "blob",
    });
    return response.data;
  },

  // === 시스템 설정 ===

  // 시스템 설정 목록 조회
  async getSettings(category?: string): Promise<SystemSettingsResponse> {
    const response = await api.get<SystemSettingsResponse>("/admin/settings", {
      params: category ? { category } : undefined,
    });
    return response.data;
  },

  // 개별 설정 조회
  async getSetting(key: string): Promise<SystemSetting> {
    const response = await api.get<SystemSetting>(`/admin/settings/${key}`);
    return response.data;
  },

  // 시스템 설정 업데이트
  async updateSettings(
    settings: Record<string, string>
  ): Promise<{ message: string; updated: number }> {
    const response = await api.put<{ message: string; updated: number }>("/admin/settings", {
      settings,
    });
    return response.data;
  },

  // 공개 설정 조회 (모든 인증 사용자 접근 가능)
  async getPublicSettings(): Promise<Record<string, string>> {
    const response = await api.get<Record<string, string>>("/admin/settings/public");
    return response.data;
  },

  // 기본 설정 초기화
  async initializeSettings(): Promise<{ message: string; initialized: boolean }> {
    const response = await api.post<{ message: string; initialized: boolean }>("/admin/settings/initialize");
    return response.data;
  },

  // === 유틸리티 ===

  // 캐시 초기화
  async clearCache(): Promise<{ message: string; cleared: boolean }> {
    const response = await api.post<{ message: string; cleared: boolean }>("/admin/cache/clear");
    return response.data;
  },

  // 시스템 상태 확인
  async getHealthStatus(): Promise<HealthCheckResponse> {
    const response = await api.get<HealthCheckResponse>("/admin/health");
    return response.data;
  },

  // === 알림 관리 ===

  // 알림 통계 조회
  async getNotificationStats(): Promise<NotificationStats> {
    const response = await api.get<NotificationStats>("/admin/notifications/stats");
    return response.data;
  },

  // 알림 목록 조회
  async getNotifications(params?: NotificationAdminFilters): Promise<NotificationAdminListResponse> {
    const response = await api.get<NotificationAdminListResponse>("/admin/notifications", { params });
    return response.data;
  },

  // 알림 삭제
  async deleteNotification(notificationId: number): Promise<void> {
    await api.delete(`/admin/notifications/${notificationId}`);
  },

  // 알림 일괄 삭제
  async bulkDeleteNotifications(notificationIds: number[]): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>("/admin/notifications/bulk", {
      data: notificationIds,
    });
    return response.data;
  },

  // 오래된 알림 정리
  async cleanupOldNotifications(days: number): Promise<{ message: string; deleted_count: number }> {
    const response = await api.delete<{ message: string; deleted_count: number }>(
      `/admin/notifications/cleanup?days=${days}`
    );
    return response.data;
  },

  // 푸시 구독 목록 조회
  async getPushSubscriptions(params?: PushSubscriptionFilters): Promise<PushSubscriptionListResponse> {
    const response = await api.get<PushSubscriptionListResponse>("/admin/notifications/push-subscriptions", { params });
    return response.data;
  },

  // 푸시 구독 삭제
  async deletePushSubscription(subscriptionId: number): Promise<void> {
    await api.delete(`/admin/notifications/push-subscriptions/${subscriptionId}`);
  },

  // 푸시 구독 활성화/비활성화 토글
  async togglePushSubscription(subscriptionId: number): Promise<{ is_active: boolean }> {
    const response = await api.put<{ is_active: boolean }>(`/admin/notifications/push-subscriptions/${subscriptionId}/toggle`);
    return response.data;
  },

  // === 이메일 발송 기록 ===

  // 이메일 발송 통계
  async getEmailLogStats(): Promise<EmailLogStats> {
    const response = await api.get<EmailLogStats>("/admin/email-logs/stats");
    return response.data;
  },

  // 이메일 발송 기록 목록
  async getEmailLogs(params?: EmailLogFilters): Promise<EmailLogListResponse> {
    const response = await api.get<EmailLogListResponse>("/admin/email-logs", { params });
    return response.data;
  },

  // ============ Scheduled Notification Config ============

  async getScheduledNotifications(): Promise<ScheduledNotificationConfig[]> {
    const response = await api.get<ScheduledNotificationConfig[]>("/admin/scheduled-notifications");
    return response.data;
  },

  async updateScheduledNotification(
    id: number,
    data: ScheduledNotificationConfigUpdate
  ): Promise<ScheduledNotificationConfig> {
    const response = await api.put<ScheduledNotificationConfig>(
      `/admin/scheduled-notifications/${id}`,
      data
    );
    return response.data;
  },

  async getExcludableDepartments(): Promise<ExcludableDepartment[]> {
    const response = await api.get<ExcludableDepartment[]>("/admin/scheduled-notifications/departments");
    return response.data;
  },

  // === 개인정보 동의 현황 ===

  async getConsentRecords(params?: ConsentRecordFilters): Promise<ConsentRecordListResponse> {
    const response = await api.get<ConsentRecordListResponse>("/admin/consent-records", { params });
    return response.data;
  },

  // === 약관/동의 문서 관리 ===

  // 현재 버전 문서 조회 (인증 사용자용)
  async getCurrentConsentDocuments(): Promise<ConsentDocument[]> {
    const response = await api.get<ConsentDocument[]>("/admin/consent-documents/current");
    return response.data;
  },

  // 문서 목록 조회 (super_admin)
  async getConsentDocuments(params?: { document_type?: string }): Promise<ConsentDocumentListResponse> {
    const response = await api.get<ConsentDocumentListResponse>("/admin/consent-documents", { params });
    return response.data;
  },

  // 문서 상세 조회 (super_admin)
  async getConsentDocument(docId: number): Promise<ConsentDocument> {
    const response = await api.get<ConsentDocument>(`/admin/consent-documents/${docId}`);
    return response.data;
  },

  // 문서 생성 (super_admin)
  async createConsentDocument(data: ConsentDocumentCreate): Promise<ConsentDocument> {
    const response = await api.post<ConsentDocument>("/admin/consent-documents", data);
    return response.data;
  },

  // 문서 수정 (super_admin, 초안만)
  async updateConsentDocument(docId: number, data: ConsentDocumentUpdate): Promise<ConsentDocument> {
    const response = await api.put<ConsentDocument>(`/admin/consent-documents/${docId}`, data);
    return response.data;
  },

  // 문서 발행 (super_admin)
  async publishConsentDocument(docId: number): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(`/admin/consent-documents/${docId}/publish`);
    return response.data;
  },

  // 문서 삭제 (super_admin, 초안만)
  async deleteConsentDocument(docId: number): Promise<void> {
    await api.delete(`/admin/consent-documents/${docId}`);
  },

  // === 시스템 설정 ===
  // 시스템 설정 조회
  async getSystemSettings(category?: string): Promise<SystemSetting[]> {
    const response = await api.get<{ items: SystemSetting[]; categories: string[] }>("/admin/settings", {
      params: { category },
    });
    return response.data.items;
  },

  // 시스템 설정 업데이트
  async updateSystemSettings(updates: Record<string, string>): Promise<{ message: string; updated_count: number }> {
    const response = await api.put<{ message: string; updated_count: number }>(
      "/admin/settings",
      { settings: updates }
    );
    return response.data;
  },
};

export default adminService;

// ============ Scheduled Notification Types ============

export interface ScheduledNotificationConfig {
  id: number;
  task_key: string;
  name: string;
  description: string | null;
  is_enabled: boolean;
  channels: string[] | null;
  schedule_type: string;
  schedule_hour: number;
  schedule_minute: number;
  schedule_day_of_week: number | null;
  schedule_day_of_month: number | null;
  excluded_department_ids: number[] | null;
  extra_config: Record<string, unknown> | null;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScheduledNotificationConfigUpdate {
  is_enabled?: boolean;
  channels?: string[];
  schedule_type?: string;
  schedule_hour?: number;
  schedule_minute?: number;
  schedule_day_of_week?: number;
  schedule_day_of_month?: number;
  excluded_department_ids?: number[];
  extra_config?: Record<string, unknown>;
}

export interface ExcludableDepartment {
  id: number;
  name: string;
  code: string;
  parent_id: number | null;
  order_num: number;
}
